<script setup lang="ts">
import {computed, onUnmounted, type PropType, ref, watch} from 'vue';
import {
    AvailabilityState,
    CabKPI,
    CabReportItem,
    enum_values,
    KpiStatusEnum,
    KpiStatusEnum_name,
    KpiStatusPrognoseEnum,
    KpiStatusPrognoseEnum_name,
    MijlpaalEnum,
    MijlpaalEnum_name,
    MijlpaalPrognoseEnum,
    MijlpaalPrognoseEnum_name,
    StatusEnum,
    StatusEnum_color,
    WaardeTypeEnum,
} from 'common';
import {downloadFile, jsonBodyFromResponse} from '@/base/utils.js';
import {
    deleteDocumentaryProof,
    getDocumentaryProof,
    updateDocumentaryProof,
    updateReportItem,
    updateReportItemAsAdmin,
} from '@/base/callBackend';
import 'element-plus/dist/index.css';
import {ElInput, ElMessage, ElOption, ElSelect} from 'element-plus';
import WaardeInvul from '@/views/general/WaardeInvul.vue';
import WaardeFixed from '@/views/general/WaardeFixed.vue';
import {stateForReporters} from '@/base/TMP_globalState.js';
import UploadIcon from '../icons/UploadIcon.vue';
import DeleteIcon from '../icons/DeleteIcon.vue';

//=================================================================================================
const emit  = defineEmits(['refreshReports']);
const props = defineProps({
    item: {
        type    : Object as PropType<CabReportItem>,
        required: true,
    },
    flagOmissions: {
        type    : Boolean,
        required: true,
    },
    isAdmin: {
        type   : Boolean,
        default: false,
    },
});

const kpi                  = ref<CabKPI>(props.item.kpi[0]);
const theUserValue         = ref<number | undefined>(props.item.reportedValue);
const theCalculatedValue   = computed<number | undefined>(() => props.item.calculatedValue);
const theReportedValue     = computed<number | undefined>(() => theUserValue.value ?? theCalculatedValue.value);
const theKpiStatus         = ref<KpiStatusEnum | undefined>(props.item.kpiStatus);
const thePrognose          = ref<number | MijlpaalPrognoseEnum | undefined>(props.item.prognose);
const theKpiStatusPrognose = ref<KpiStatusPrognoseEnum | undefined>(props.item.kpiStatusPrognose);
const statusChanged        = ref<boolean>(false);
const documentaryProofName = ref<string | undefined>(props.item.documentaryProofName);

//=================================================================================================
watch(theUserValue, async (v) => {
    await reportedValueChanged(v);
    if (kpi.value.isMijlpaal()) {
        statusChanged.value = true;
    }
});
watch(theKpiStatus, async (v) => {
    await kpiStatusValueChanged(v);
});
watch(thePrognose, async (v) => {
    await prognoseChanged(v);
});
watch(theKpiStatusPrognose, async (v) => {
    await kpiStatusPrognoseChanged(v);
});

onUnmounted(() => {
    if (statusChanged.value) {
        emit('refreshReports');
    }
});

//=================================================================================================
async function reportedValueChanged(v: number | MijlpaalEnum | undefined) {
    if (v !== theUserValue.value || v !== props.item.reportedValue) {
        theUserValue.value       = v;
        props.item.reportedValue = v;
        await onChange();
    }
}

async function kpiStatusValueChanged(v: KpiStatusEnum | undefined) {
    if (v !== theKpiStatus.value || v !== props.item.kpiStatus) {
        theKpiStatus.value   = v;
        props.item.kpiStatus = v;
        await onChange();
    }
}

async function prognoseChanged(v: number | MijlpaalPrognoseEnum | undefined) {
    if (v !== thePrognose.value || v !== props.item.prognose) {
        thePrognose.value   = v;
        props.item.prognose = v;
        await onChange();
    }
}

async function kpiStatusPrognoseChanged(v: KpiStatusPrognoseEnum | undefined) {
    if (v !== theKpiStatusPrognose.value || v !== props.item.kpiStatusPrognose) {
        theKpiStatusPrognose.value   = v;
        props.item.kpiStatusPrognose = v;
        await onChange();
    }
}

function documentaryProofChanged(name?: string) {
    if (name !== documentaryProofName.value || name !== props.item.documentaryProofName) {
        documentaryProofName.value      = name;
        props.item.documentaryProofName = name;
    }
}

async function keyupOnTheValue(event: any) {
    if (event.key === 'Backspace' || event.key === 'Delete') {
        theUserValue.value = undefined;
        event.target.blur();
    }
}

function keyupOnTheKpiStatus(event: any) {
    if (event.key === 'Backspace' || event.key === 'Delete') {
        theKpiStatus.value = undefined;
        event.target.blur();
    }
}

function keyupOnThePrognose(event: any) {
    if (event.key === 'Backspace' || event.key === 'Delete') {
        thePrognose.value = undefined;
        event.target.blur();
    }
}

function keyupOnTheKpiStatusPrognose(event: any) {
    if (event.key === 'Backspace' || event.key === 'Delete') {
        theKpiStatusPrognose.value = undefined;
        event.target.blur();
    }
}

function isWaardeCalculated(): boolean {
    return !isMijlpaal() && kpi.value.isCalculatedValue;
}

function isStatusCalculated(): boolean {
    return !isMijlpaal() && !!kpi.value.gehaaldExpression;
}

async function onChange() {
    if (isMijlpaal() && props.item.reportedValue === MijlpaalEnum.gehaald || props.item.reportedValue === MijlpaalEnum.gestart) {
        props.item.mitigatie = '';
        thePrognose.value    = undefined;
        props.item.prognose  = undefined;
    }
    const newItem = props.isAdmin
        ? await updateReportItemAsAdmin(props.item)
        : await updateReportItem(props.item);
    if (newItem) {
        props.item.calculatedKpiStatus = newItem.calculatedKpiStatus;
    }
}

async function uploadDocumentaryProof(uuid: string) {
    const input         = document.createElement('input');
    input.type          = 'file';
    input.style.display = 'none';
    input.accept        = '.pdf';
    input.addEventListener('change', async (event: Event) => {
        const target = event.target as HTMLInputElement;
        const file   = target.files?.[0] ?? null;
        if (file) {
            const formData = new FormData();
            formData.append('file', file);
            const res  = await updateDocumentaryProof(uuid, formData);
            const body = await jsonBodyFromResponse(res);
            if (res.ok) {
                ElMessage({
                    message : `Bewijsstuk ${documentaryProofName.value ? 'vervangen' : 'toegevoegd'}.`,
                    type    : 'success',
                    duration: 10000,
                });
                documentaryProofChanged(file.name);
            } else {
                console.error(body.message);
                ElMessage({
                    message : `Upload mislukt: (${body.message})`,
                    type    : 'error',
                    duration: 10000,
                });
            }
        }
        document.body.removeChild(input);
    });
    document.body.appendChild(input);
    input.click();

}

//=================================================================================================
function open_or_overdue() {
    return props.item.status === StatusEnum.open || props.item.status === StatusEnum.overdue;
}

function reportersCanWrite() {
    if (props.isAdmin) {
        return open_or_overdue();
    }
    return open_or_overdue() && stateForReporters.value === AvailabilityState.write;
}

function isMijlpaal() {
    return props.item?.isMijlpaal;
}

//=================================================================================================

function dynColor() {
    const status = props.item.status;
    const text   = status === StatusEnum.submitted || status === StatusEnum.accepted ? 'text-gray-500' : '';
    return 'bg-' + StatusEnum_color(status) + ' ' + text;
}

function dynClassForLabelColumn(first: boolean = true) {
    const w = first ? 'w-44' : 'w-28';
    return `shrink-0 ${w} mr-4 py-1 text-right`;
}

function omissionColor(v: any) {
    return props.flagOmissions && (v === undefined || v === '') ? 'outline outline-3 outline-red-500' : '';
}

//=================================================================================================
</script>

<template>
    <div class="flex flex-col justify-start items-stretch pr-4" :class="dynColor()">

        <div class="flex flex-col space-y-1 ml-10 my-8">
            <!--+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++-->
            <div v-if="isMijlpaal() && item.showReportedValue()"
                 class="flex flex-row justify-start items-center py-1">
                <div :class="dynClassForLabelColumn()" v-if="open_or_overdue()">
                    ik rapporteer
                </div>
                <div :class="dynClassForLabelColumn()" v-else>
                    ik rapporteerde
                </div>

                <div class="font-bold w-72">
                    <el-select
                        :class="omissionColor(theUserValue)"
                        v-model="theUserValue"
                        placeholder="kies een optie"
                        style="min-width: 210px"
                        :disabled="!reportersCanWrite()"
                        @keyup="keyupOnTheValue($event)"
                    >
                        <el-option
                            v-for="item in enum_values(MijlpaalEnum)"
                            :key="item"
                            :label="MijlpaalEnum_name(item)"
                            :value="item"
                        />
                    </el-select>
                </div>
            </div>
            <!--+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++-->
            <div v-if="!isMijlpaal() && (item.showReportedValue() || item.showKpiStatus())"
                 class="flex flex-row justify-start items-center py-1">

                <template v-if="item.showReportedValue()">
                    <template v-if="isWaardeCalculated()">
                        <div :class="dynClassForLabelColumn()">
                            berekend als
                        </div>
                        <WaardeFixed
                            v-model:waarde="theReportedValue"
                            :waarde-type="kpi.waardeType"
                            class="font-bold"
                        />
                    </template>
                    <template v-else>
                        <div :class="dynClassForLabelColumn()" v-if="open_or_overdue()">
                            ik rapporteer
                        </div>
                        <div :class="dynClassForLabelColumn()" v-else>
                            ik rapporteerde
                        </div>
                        <WaardeInvul
                            v-model:waarde="theUserValue"
                            :waarde-type="kpi.waardeType"
                            class="font-bold"
                            :class="isWaardeCalculated()?'':omissionColor(theUserValue)"
                            mag-onbekend
                            :disabled="!reportersCanWrite()"
                        />
                    </template>
                </template>

                <template v-if="item.showKpiStatus() && !isStatusCalculated()">
                    <div :class="dynClassForLabelColumn()">
                        met status
                    </div>
                    <div class="font-bold w-72">
                        <el-select
                            :class="omissionColor(theKpiStatus)"
                            v-model="theKpiStatus"
                            placeholder="kies een optie"
                            style="min-width: 210px"
                            :disabled="!reportersCanWrite()"
                            @keyup="keyupOnTheKpiStatus($event)"
                        >
                            <el-option
                                v-for="item in enum_values(KpiStatusEnum)"
                                :key="item"
                                :label="KpiStatusEnum_name(item)"
                                :value="item"
                            />
                        </el-select>
                    </div>
                </template>

                <template v-if="item.showCalculatedKpiStatus()">
                    <div :class="dynClassForLabelColumn()">
                        berekende status
                    </div>
                    <div class="font-bold">
                        {{ KpiStatusEnum_name(item.calculatedKpiStatus) }}
                    </div>
                </template>

            </div>
            <!--+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++-->
            <div class="flex flex-row justify-start items-center py-1"
                 v-if="item.showPrognose() || item.showKpiStatusPrognose()">
                <template v-if="item.showPrognose()">
                    <div v-if="isMijlpaal()"
                         class="flex flex-row justify-start items-start">
                        <div :class="dynClassForLabelColumn()">
                            prognose
                        </div>
                        <div class="font-bold w-72">
                            <el-select
                                :class="omissionColor(thePrognose)"
                                v-model="thePrognose"
                                placeholder="kies een optie"
                                style="min-width: 270px"
                                :disabled="!reportersCanWrite()"
                                @keyup="keyupOnThePrognose($event)"
                            >
                                <el-option
                                    v-for="item in enum_values(MijlpaalPrognoseEnum)"
                                    :key="item"
                                    :label="MijlpaalPrognoseEnum_name(item)"
                                    :value="item"
                                />
                            </el-select>
                        </div>
                    </div>

                    <div v-if="!isMijlpaal()"
                         class="flex flex-row justify-start items-baseline">
                        <div :class="dynClassForLabelColumn()">
                            prognose
                        </div>
                        <WaardeInvul
                            v-model:waarde="thePrognose"
                            :waarde-type="kpi.waardeType"
                            class="font-bold"
                            :class="omissionColor(thePrognose)"
                            mag-onbekend
                        />
                    </div>
                </template>

                <template v-if="item.showKpiStatusPrognose()">
                    <div :class="dynClassForLabelColumn()">
                        met status
                    </div>

                    <div class="font-bold w-72">
                        <el-select
                            :class="omissionColor(theKpiStatusPrognose)"
                            v-model="theKpiStatusPrognose"
                            placeholder="kies een optie"
                            style="min-width: 270px"
                            :disabled="!reportersCanWrite()"
                            @keyup="keyupOnTheKpiStatusPrognose($event)"
                        >
                            <el-option
                                v-for="item in enum_values(KpiStatusPrognoseEnum)"
                                :key="item"
                                :label="KpiStatusPrognoseEnum_name(item)"
                                :value="item"
                            />
                        </el-select>
                    </div>
                </template>

            </div>
            <!--+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++-->
            <div class="flex flex-row justify-start items-center py-1"
                 v-if="item.showMitigatie()">
                <div :class="dynClassForLabelColumn()">
                    mitigatie
                </div>
                <div class="font-bold w-full">
                    <el-input
                        :class="omissionColor(item.mitigatie)"
                        v-model="item.mitigatie"
                        @input="onChange()"
                        type="textarea"
                        :autosize="{ minRows: 1, maxRows: 5 }"
                        :disabled="!reportersCanWrite()"
                    />
                </div>
            </div>
            <!--+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++-->
            <div class="flex flex-row justify-start items-center py-1" v-if="item.showDocumentaryProof(false)">
                <div :class="dynClassForLabelColumn()">
                    bewijsstuk
                </div>
                <div class="bg-white/40 px-2 py-1" :class="omissionColor(documentaryProofName)">
                    <a v-if="documentaryProofName"
                       href="#"
                       @click.prevent="getDocumentaryProof(item.uuid).then((res:Response)=>downloadFile(res, documentaryProofName!))"
                       class="text-blue-500 hover:text-blue-700">
                        {{ documentaryProofName }}
                    </a>
                    <span v-else>none</span>
                </div>
                <DeleteIcon v-if="documentaryProofName && open_or_overdue() && reportersCanWrite()"
                            @click.prevent="deleteDocumentaryProof(item.uuid).then(() => documentaryProofChanged(undefined))">
                </DeleteIcon>
                <UploadIcon v-if="open_or_overdue() && reportersCanWrite()"
                            :title="documentaryProofName?'vervang':'bijvoegen'"
                            @click.prevent="uploadDocumentaryProof(item.uuid)">
                </UploadIcon>
            </div>
            <!--+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++-->
            <div class="flex flex-row justify-start items-center py-1">
                <div :class="dynClassForLabelColumn()">
                    opmerkingen
                </div>
                <div class="font-bold w-full">
                    <el-input
                        :class="omissionColor(documentaryProofName && (!item.opmerking || item.opmerking.trim().length<1)? undefined : true)"
                        v-model="item.opmerking"
                        @input="onChange()"
                        type="textarea"
                        :autosize="{ minRows: 1, maxRows: 5 }"
                        :disabled="!reportersCanWrite()"
                    />
                </div>
            </div>
            <!--+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++-->
            <div class="flex flex-row justify-start items-center py-1"
                 v-if="!!kpi.omschrijving">
                <div :class="dynClassForLabelColumn()">
                    omschrijving
                </div>
                <div class="bg-white/50 px-2 w-full">
                    {{ kpi.omschrijving }}
                </div>
            </div>
            <!--+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++-->
            <div class="flex flex-row justify-start items-center py-1"
                 v-if="item.showDoelwaarde(false) || (isStatusCalculated() && kpi.hasDoelwaarde)">
                <div :class="dynClassForLabelColumn()">
                    doelwaarde
                </div>
                <WaardeFixed
                    :waarde="item.doelwaarde"
                    :waarde-type="kpi.waardeType"/>
            </div>
            <!--+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++-->
            <div class="flex flex-row justify-start items-center py-1"
                 v-if="item.showVecozoId(false) || item.showUitkeringsbedrag(false)">
                <div :class="dynClassForLabelColumn()">
                    uitkeringsbedrag
                </div>
                <WaardeFixed
                    :waarde="item.uitkeringsbedrag"
                    :waarde-type="WaardeTypeEnum.bedrag"
                />
                <div :class="dynClassForLabelColumn(false)">
                    vecozo id
                </div>
                <div class="bg-white/40 px-2 py-1">
                    {{ item.vecozoId }}
                </div>
            </div>
            <!--+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++-->
            <div class="flex flex-row justify-start items-center py-1"
                 v-if="kpi.isMijlpaal() && item.showUitkeringsbedrag(false) && !item.onlyPrognose">
                <div class="flex flex-row items-center">
                    <div :class="dynClassForLabelColumn()">
                        voor (tegoed / uitgekeerd)
                    </div>
                    <WaardeFixed
                        v-model:waarde="item.nogUitTeKerenVoorToekenningBedrag"
                        :waarde-type="WaardeTypeEnum.bedrag"
                    />
                    &nbsp;/&nbsp;
                    <WaardeFixed
                        v-model:waarde="item.alUitgekeerdVoorToekenningBedrag"
                        :waarde-type="WaardeTypeEnum.bedrag"
                    />
                </div>
                <div class="flex flex-row items-center">
                    <div :class="dynClassForLabelColumn(false)">
                        aangevraagd
                    </div>
                    <WaardeFixed
                        v-model:waarde="item.aangevraagdBedrag"
                        :waarde-type="WaardeTypeEnum.bedrag"
                    />
                </div>
                <div class="flex flex-row items-center">
                    <div :class="dynClassForLabelColumn(false)">
                        toegekend
                    </div>
                    <WaardeFixed
                        v-model:waarde="item.toegekendBedrag"
                        :waarde-type="WaardeTypeEnum.bedrag"
                    />
                </div>
                <div class="flex flex-row items-center">
                    <div :class="dynClassForLabelColumn()">
                        na (tegoed / uitgekeerd)
                    </div>
                    <WaardeFixed
                        v-model:waarde="item.nogUitTeKerenNaToekenningBedrag"
                        :waarde-type="WaardeTypeEnum.bedrag"
                    />
                    &nbsp;/&nbsp;
                    <WaardeFixed
                        v-model:waarde="item.alUitgekeerdNaToekenningBedrag"
                        :waarde-type="WaardeTypeEnum.bedrag"
                    />
                </div>

            </div>
            <!--+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++-->
            <div class="flex flex-row justify-start items-center py-1"
                 v-if="!kpi.isMijlpaal() && item.showUitkeringsbedrag(false) && !item.onlyPrognose">
                <div class="flex flex-row items-center">
                    <div :class="dynClassForLabelColumn(true)">
                        aangevraagd
                    </div>
                    <WaardeFixed
                        v-model:waarde="item.aangevraagdBedrag"
                        :waarde-type="WaardeTypeEnum.bedrag"
                    />
                </div>
                <div class="flex flex-row items-center">
                    <div :class="dynClassForLabelColumn(false)">
                        toegekend
                    </div>
                    <WaardeFixed
                        v-model:waarde="item.toegekendBedrag"
                        :waarde-type="WaardeTypeEnum.bedrag"
                    />
                </div>
            </div>
            <!--+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++-->
        </div>

    </div>
</template>

<style></style>
